// src/pages/BuildingViewer.jsx
import { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js';
import { FLOOR_CLIP } from './FloorViewer';
import useIsMobile from '../../utils/windows';

import styles from '../Simulation.module.css';

const SHOW_HUDVIEW_DEBUG_PANEL = false;
const SHOW_OPACITY_DEBUG_PANEL = false;

/* ------------------------------ paths ------------------------------ */
function glbPathForFloor(f) {
    if (f === 'B2') return '/models/B2.glb';
    if (f === 'B1') return '/models/B1.glb';
    return `/models/F${f}.glb`;
}
function stairPath() {
    return '/models/stairs.glb';
}

/* ------------------------------ consts ------------------------------ */
const ALL_FLOORS = ['B2', 'B1', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12', '13', '14', '15', '16'];
const DOMAIN = { minX: 0, maxX: 30, minZ: -41, maxZ: 0, cx: 15, cz: -20.5 };

// DARK_BACKGROUND
const LIGHT_GRAY_HEX = 0xf8fafc;
const SCENE_BG_HEX = 0x0f172a;
// DARK_BACKGROUND

const ROUTE_VIEW_INIT = false;
const PEEK_PX = 25; // 패널 숨김 시 아래에 남겨둘 상단 노출 높이(px)

const DEFAULT_PALETTE = [0xef4444, 0xf59e0b, 0x10b981, 0x3b82f6, 0x8b5cf6, 0xec4899, 0x14b8a6, 0xf97316, 0x84cc16, 0x06b6d4, 0xa855f7, 0x22c55e];
function colorFromKey(key, palette = DEFAULT_PALETTE) {
    let h = 2166136261 >>> 0;
    for (let i = 0; i < key.length; i++) {
        h ^= key.charCodeAt(i);
        h = Math.imul(h, 16777619);
    }
    return palette[h % palette.length];
}
const SHOW_FULL_ROUTE_IN_FLOOR_MODE = false;

/* ------------------------------ utils ------------------------------ */
function snapshotView(camera, controls) {
    return { pos: camera.position.clone(), target: controls.target.clone() };
}
function restoreView(camera, controls, v) {
    if (!v) return;
    camera.position.copy(v.pos);
    controls.target.copy(v.target);
    camera.updateProjectionMatrix();
    controls.update();
}
function toFixedView(camera, controls) {
    const p = camera.position,
        t = controls.target,
        r = (v) => Number(v.toFixed(3));
    return { pos: [r(p.x), r(p.y), r(p.z)], target: [r(t.x), r(t.y), r(t.z)] };
}
function overrideGroupLightGray(group, hex = LIGHT_GRAY_HEX) {
    group.traverse((o) => {
        if (!o.isMesh) return;
        const newMat = new THREE.MeshStandardMaterial({
            color: hex,
            metalness: 0.1,
            roughness: 0.6,
            side: THREE.DoubleSide,
        });
        const mats = Array.isArray(o.material) ? o.material : [o.material];
        mats.forEach((m) => m?.dispose?.());
        o.material = newMat;
    });
}
function normalizeFloorId(f) {
    if (f == null) return '';
    let s = String(f).trim().toUpperCase();
    if (s.startsWith('F')) s = s.slice(1);
    if (s.startsWith('B')) {
        const n = parseInt(s.slice(1), 10);
        return Number.isFinite(n) ? `B${n}` : s;
    }
    const n = parseInt(s, 10);
    return Number.isFinite(n) ? String(n) : s;
}
function makeStartMarkerMesh(color = 0xfacc15) {
    // 기본 파란색 (#3B82F6)
    // 출발: 파란색 사각형 마커
    const geom = new THREE.BoxGeometry(0.7, 0.7, 0.7);
    const mat = new THREE.MeshBasicMaterial({
        color,
        transparent: true,
        opacity: 1.0,
        depthTest: false,
        depthWrite: false,
    });

    const mesh = new THREE.Mesh(geom, mat);
    mesh.renderOrder = 1800;
    return mesh;
}
function makeEndMarkerMesh(color = 0xff6a00) {
    // 도착: 주황색, 아래쪽을 향하는 정삼각형 모양(3각 콘)
    // ConeGeometry(반지름, 높이, radialSegments=3) → 정삼각형 단면
    const geom = new THREE.ConeGeometry(0.7, 1.1, 3);

    // 기본은 +Y 방향으로 뾰족 → 180도 회전해서 -Y(아래)를 향하게
    geom.rotateX(Math.PI);

    const mat = new THREE.MeshBasicMaterial({
        color,
        transparent: true,
        opacity: 1.0,
        depthTest: false,
        depthWrite: false,
    });

    const mesh = new THREE.Mesh(geom, mat);
    mesh.renderOrder = 1810; // 살짝 더 위에 렌더링
    return mesh;
}

/* ------------------------------ component ------------------------------ */
export default function BuildingViewer({
    selectedFloor = 'B1',
    initialMode = 'building',
    fixedInitialView = {
        pos: [87.157, 57.993, 27.443],
        target: [13.708, 23.054, -20.797],
    },
    onModeChange,
    onViewChange,
    overlayByFloor = {},
    overlaySize = { w: 30, h: 41 },
    overlayHeight = 1.8,
    showGrid = true,
    className,
    simulationId,
    send,
    routesPayload,
    controlledByFloor = {},
    currentMetric = 'co',
    overlaysByMetric = { co: {}, temp: {}, vis: {} },
    renderMeasureRef,
    onRouteSelected,
    onRouteCleared,
}) {
    const mountRef = useRef(null);
    const stateRef = useRef({});
    const isMobile = useIsMobile();

    const [mode, setMode] = useState(initialMode);
    const [showRoutes, setShowRoutes] = useState(ROUTE_VIEW_INIT);
    const [showBaseSections, setShowBaseSections] = useState(true);

    // 평면모드(층뷰에서만 표시/동작)
    const [planMode, setPlanMode] = useState(false);
    const [routeViewMode, setRouteViewMode] = useState('fromSelectedFloor'); //"all" | "fromSelectedFloor"

    // 컨투어 바닥모드 (기본 활성화)
    const [contourFloorModeEnabled, setContourFloorModeEnabled] = useState(true);

    // 하단 패널: 내부 토글(기본 숨김) + 피크
    const [panelOpen, setPanelOpen] = useState(false);

    // 하단 카메나 패널
    const [hudOpen] = useState(false);
    const [hudView, setHudView] = useState(null);

    // 모드별 5가지 투명도 (UI 표시 단위: 0~100)
    const [alphaUiByMode, setAlphaUiByMode] = useState({
        building: {
            glb: 100,
            stairs: 100,
            sections: 20,
            routes: 100,
            contour: 100,
        },
        floor: { glb: 100, stairs: 20, sections: 50, routes: 100, contour: 100 },
    });
    const curAlphaUi = alphaUiByMode[mode] ?? alphaUiByMode.building;

    const changeMode = (m) => {
        setMode(m);
        onModeChange?.(m);
    };

    // === 미니맵: 위/아래 두 개 캔버스
    const miniTopRef = useRef(null);
    const miniBottomRef = useRef(null);
    const miniStateRef = useRef({
        top: { renderer: null, scene: null, cam: null, plane: null, grid: null },
        bottom: { renderer: null, scene: null, cam: null, plane: null, grid: null },
    });

    // 미니맵 공용 생성
    function makeMiniScene(canvasEl) {
        const r = new THREE.WebGLRenderer({
            canvas: canvasEl,
            antialias: true,
            alpha: true,
        });
        r.setClearColor(0x000000, 0);
        const DPR = Math.min(window.devicePixelRatio || 1, 2);
        r.setPixelRatio(DPR);
        const { clientWidth: W = 1, clientHeight: H = 1 } = canvasEl || {};
        r.setSize(W, H, false);

        const s = new THREE.Scene();
        s.background = null;
        const aspect = W / Math.max(1, H);
        const frustumHeight = Math.max(overlaySize.h, overlaySize.w) * 1.2;
        const top = frustumHeight / 2,
            bottom = -top;
        const right = top * aspect,
            left = -right;
        const cam = new THREE.OrthographicCamera(left, right, top, bottom, 0.1, 10000);
        cam.up.set(0, 0, -1);
        cam.position.set(DOMAIN.cx, 500, DOMAIN.cz);
        cam.lookAt(new THREE.Vector3(DOMAIN.cx, 0, DOMAIN.cz));
        cam.updateProjectionMatrix();

        // grid
        let gridLines = null;
        const mat = new THREE.LineBasicMaterial({
            transparent: true,
            opacity: 0.35,
        });
        const geom = new THREE.BufferGeometry();
        const step = 5,
            verts = [];
        for (let x = DOMAIN.minX; x <= DOMAIN.maxX; x += step) verts.push(x, 0.02, DOMAIN.minZ, x, 0.02, DOMAIN.maxZ);
        for (let z = DOMAIN.maxZ; z >= DOMAIN.minZ; z -= step) verts.push(DOMAIN.minX, 0.02, z, DOMAIN.maxX, 0.02, z);
        geom.setAttribute('position', new THREE.BufferAttribute(new Float32Array(verts), 3));
        gridLines = new THREE.LineSegments(geom, mat);
        gridLines.renderOrder = 5;
        s.add(gridLines);

        // overlay plane
        const g = new THREE.PlaneGeometry(1, 1);
        g.rotateX(-Math.PI / 2);
        const m = new THREE.MeshBasicMaterial({
            map: null,
            transparent: true,
            opacity: 1.0,
            depthWrite: false,
            depthTest: false,
        });
        const plane = new THREE.Mesh(g, m);
        plane.position.set(DOMAIN.cx, overlayHeight + 0.02, DOMAIN.cz);
        plane.scale.set(overlaySize.w, 1, overlaySize.h);
        plane.renderOrder = 1500;
        s.add(plane);

        return { renderer: r, scene: s, cam, plane, grid: gridLines };
    }

    useEffect(() => {
        if (!renderMeasureRef?.current?.start) return;

        const start = renderMeasureRef.current.start;
        const frameIndex = renderMeasureRef.current.frameIndex;

        // React commit 이후 실제 브라우저 페인트까지 기다렸다가 측정
        requestAnimationFrame(() => {
            const end = performance.now();
            const dt = end - start;

            // console.log(`[Measure] BuildingViewer render+paint${frameIndex != null ? ` (frame ${frameIndex})` : ''}: ${dt.toFixed(1)} ms`);

            // 한 프레임 측정 후 초기화
            renderMeasureRef.current.start = 0;
        });
    }, [
        overlayByFloor, // 컨투어 텍스처 변경
        routesPayload, // 경로 라인 변경
        overlaysByMetric, // 미니맵 텍스처 변경
        controlledByFloor, // 제어구역 Extrude mesh 변경
        currentMetric, // 지표 변경으로 인한 오버레이 교체
    ]);

    useEffect(() => {
        const s = stateRef.current;
        if (!s) return;
        // routeViewMode 내부 상태 반영
        s.routeViewMode = routeViewMode;

        // 라인 가시성 (층별/전체)
        s.updateEdgeVisibility && s.updateEdgeVisibility();
    }, [routeViewMode]);

    // 컨투어 바닥모드 on/off 시 셰이더 uniform 갱신
    useEffect(() => {
        const s = stateRef.current;
        if (!s) return;

        s.contourFloorModeEnabled = contourFloorModeEnabled;

        s.overlayPlanes?.forEach((plane) => {
            const mat = plane?.material;
            const shader = mat?.userData?.shader;
            if (shader && shader.uniforms?.uZeroToTransparent) {
                // 활성화: 0 → 바닥 전체 칠함(기존과 동일)
                // 비활성화: 0 값인 픽셀은 alpha=0 으로 감춤
                shader.uniforms.uZeroToTransparent.value = contourFloorModeEnabled ? 0.0 : 1.0;
            }
        });
    }, [contourFloorModeEnabled]);

    useEffect(() => {
        const mount = mountRef.current;
        if (!mount) return;

        /* ---------------- scene & renderer ---------------- */
        const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
        const DPR = Math.min(window.devicePixelRatio || 1, 2);
        renderer.setPixelRatio(DPR);
        renderer.setSize(mount.clientWidth || 1, mount.clientHeight || 1, false);
        renderer.localClippingEnabled = true;
        mount.appendChild(renderer.domElement);
        Object.assign(renderer.domElement.style, {
            width: '100%',
            height: '100%',
            display: 'block',
        });

        const scene = new THREE.Scene();
        scene.background = new THREE.Color(SCENE_BG_HEX);

        // lights
        const dir = new THREE.DirectionalLight(0xffffff, 0.9);
        dir.position.set(200, 400, 250);
        scene.add(dir);
        scene.add(new THREE.AmbientLight(0xffffff, 0.5));

        // roots
        const buildingRoot = new THREE.Group();
        scene.add(buildingRoot);
        const floorsRoot = new THREE.Group();
        buildingRoot.add(floorsRoot);

        // ✅ 컨투어는 별도 씬 (항상 최상단)
        const overlayScene = new THREE.Scene();
        overlayScene.background = null;
        const overlaysContour = new THREE.Group();
        overlayScene.add(overlaysContour);

        const routeMarkersRoot = new THREE.Group();
        overlayScene.add(routeMarkersRoot);

        // 경로 / 전체링크 / 섹션
        const edgesRoot = new THREE.Group();
        edgesRoot.renderOrder = 900;
        buildingRoot.add(edgesRoot);
        const baseEdgesRoot = new THREE.Group();
        baseEdgesRoot.renderOrder = 800;
        buildingRoot.add(baseEdgesRoot);
        const baseSectionsRoot = new THREE.Group();
        baseSectionsRoot.renderOrder = 850;
        buildingRoot.add(baseSectionsRoot);
        const controlledFloorMeshes = new Map(); // floor -> Map<sectionId, THREE.Mesh>
        const controlledOverlayRoot = new THREE.Group();
        overlayScene.add(controlledOverlayRoot);

        const floorEdges = new Map();
        const baseFloorEdges = new Map();
        const baseFloorSectionsMesh = new Map();
        const sectionById = new Map();

        let stairsGroup = null; // 계단 GLB

        // loader & stores
        const loader = new GLTFLoader();
        const floorGroups = new Map();
        const overlayPlanes = new Map();
        const floorMeta = new Map();
        stateRef.current.loadGen = 0;

        /* ---------------- cameras & controls ---------------- */
        // 초기: Perspective
        const perspCam = new THREE.PerspectiveCamera(45, (mount.clientWidth || 1) / Math.max(1, mount.clientHeight || 1), 0.1, 10000);
        const initialPos = new THREE.Vector3(DOMAIN.cx + 80, 160, DOMAIN.cz + 120);
        const initialTgt = new THREE.Vector3(DOMAIN.cx, 20, DOMAIN.cz);
        if (fixedInitialView?.pos && fixedInitialView?.target) {
            perspCam.position.set(...fixedInitialView.pos);
            perspCam.lookAt(new THREE.Vector3(...fixedInitialView.target));
        } else {
            perspCam.position.copy(initialPos);
            perspCam.lookAt(initialTgt);
        }
        let controls = new OrbitControls(perspCam, renderer.domElement);
        controls.enableDamping = true;
        controls.dampingFactor = 0.08;
        controls.zoomSpeed = 0.7;
        controls.target.copy(fixedInitialView?.target ? new THREE.Vector3(...fixedInitialView.target) : initialTgt);
        controls.update();

        // 현재 카메라/컨트롤은 stateRef를 통해 참조 (렌더/피킹/리사이즈 모두 여기만 봄)
        stateRef.current.camera = perspCam;
        stateRef.current.controls = controls;

        // HUD 업데이트 (현재 카메라/컨트롤 사용)
        const updateHUD = () => {
            const s = stateRef.current;
            if (!s) return;
            const v = toFixedView(s.camera, s.controls);
            setHudView(v);
            onViewChange?.(v);
        };
        controls.addEventListener('change', updateHUD);
        updateHUD();

        // 컨트롤 변경 감지 (클릭 판정용)
        let hadControlChange = false;
        const onControlsChange = () => {
            hadControlChange = true;
        };
        controls.addEventListener('change', onControlsChange);

        // 바닥 그리드
        let gridLines = null;
        if (showGrid) {
            const mat = new THREE.LineBasicMaterial({
                color: 0xb6c2cf,
                transparent: true,
                opacity: 0.35,
                depthWrite: false,
            });
            const geom = new THREE.BufferGeometry();
            const step = 5,
                verts = [];
            for (let x = DOMAIN.minX; x <= DOMAIN.maxX; x += step) verts.push(x, 0.02, DOMAIN.minZ, x, 0.02, DOMAIN.maxZ);
            for (let z = DOMAIN.maxZ; z >= DOMAIN.minZ; z -= step) verts.push(DOMAIN.minX, 0.02, z, DOMAIN.maxX, 0.02, z);
            geom.setAttribute('position', new THREE.BufferAttribute(new Float32Array(verts), 3));
            gridLines = new THREE.LineSegments(geom, mat);
            gridLines.renderOrder = 5;
            scene.add(gridLines);
        }

        /* ---------------- visibility policy ---------------- */
        function updateEdgeVisibility() {
            const s = stateRef.current;
            const curMode = s?.mode ?? mode;
            const curSel = normalizeFloorId(s?.selectedFloor ?? selectedFloor);
            const showR = !!(s?.showRoutes ?? true);
            const showS = !!(s?.showBaseSections ?? true);
            const brMode = s?.routeViewMode ?? routeViewMode; // "all" | "fromSelectedFloor"

            edgesRoot.visible = showR;
            baseEdgesRoot.visible = false;
            baseSectionsRoot.visible = showS;
            controlledOverlayRoot.visible = true;

            if (routeMarkersRoot) {
                routeMarkersRoot.visible = showR;
            }

            // 경로 라인 가시성
            floorEdges.forEach((val, f) => {
                const arr = Array.isArray(val) ? val : [val];
                const floorId = normalizeFloorId(f);

                arr.forEach((mesh) => {
                    let visible = showR;

                    if (!showR) {
                        visible = false;
                    } else if (curMode === 'floor' || brMode === 'fromSelectedFloor') {
                        // 층뷰: 현재 선택한 층만
                        visible = !SHOW_FULL_ROUTE_IN_FLOOR_MODE ? floorId === curSel : true;
                    } else {
                        // building + "all"
                        visible = true;
                    }

                    mesh.visible = visible;
                });
            });

            // 바닥 섹션은 기존 로직 유지
            baseFloorSectionsMesh.forEach((mesh, f) => {
                const visibleByMode = curMode === 'floor' ? normalizeFloorId(f) === curSel : true;
                mesh.visible = showS && visibleByMode;
            });

            // 통제 구역 mesh는 전체 보이도록 유지 (필요시 여기서도 필터링 가능)
            controlledFloorMeshes.forEach((idMap, f) => {
                idMap.forEach((mesh) => {
                    mesh.visible = true;
                });
            });

            // 라인 두께 + 마커 표시 로직을 같은 타이밍에 적용
            const s2 = stateRef.current;
            s2?.applyRouteSelectionStyle?.();
        }

        function buildSingleSectionMesh(ring, color = 0xef4444, yOffset = 0.022, opacity = 0.5) {
            const geom = buildMergedSectionsGeometryForFloor([ring], yOffset); // 기존 삼각분할 재사용
            if (!geom) return null;
            const mat = new THREE.MeshStandardMaterial({
                color,
                metalness: 0,
                roughness: 1,
                transparent: true,
                opacity,
                depthWrite: false,
                depthTest: false,
            });
            const mesh = new THREE.Mesh(geom, mat);
            mesh.renderOrder = 860;
            return mesh;
        }

        function buildExtrudedControlledMesh(ring, { height = 3.6, color = 0xef4444, opacity = 0.35, curveSegments = 8 } = {}) {
            if (!ring || ring.length < 3) return null;

            const z0 = ring[0].z || 0; // 층 높이(월드 Y)
            const eps = 0.022; // 기존 yOffset과 유사한 살짝 띄움

            // 1) 폴리곤을 XY에 그리되, 화면 규칙에 맞춰 y -> -p.y
            const N = ring.length;
            const same = (a, b) => Math.abs(a - b) < 1e-6;
            const isClosed = same(ring[0].x, ring[N - 1].x) && same(ring[0].y, ring[N - 1].y);
            const loop = isClosed ? ring.slice(0, N - 1) : ring;
            if (loop.length < 3) return null;

            const shape = new THREE.Shape();
            shape.moveTo(loop[0].x, -loop[0].y);
            for (let i = 1; i < loop.length; i++) shape.lineTo(loop[i].x, -loop[i].y);

            // 2) Extrude: 로컬 +Z로 높이를 만든 뒤
            const geom = new THREE.ExtrudeGeometry(shape, {
                depth: -height,
                bevelEnabled: false,
                steps: 1,
                curveSegments,
            });

            // 3) 로컬 Z(두께) -> 월드 Y(높이)
            geom.rotateX(Math.PI / 2);

            // 4) 바닥을 해당 층 높이에 맞추기 (월드 Y만 이동)
            geom.translate(0, z0 + eps, 0);

            const mat = new THREE.MeshBasicMaterial({
                color,
                transparent: true,
                opacity,
                depthWrite: false,
                depthTest: false,
                polygonOffset: true,
                polygonOffsetFactor: -1,
                side: THREE.DoubleSide,
            });

            const mesh = new THREE.Mesh(geom, mat);
            mesh.renderOrder = 1500;
            return mesh;
        }

        function clearControlledForFloor(floor) {
            const map = controlledFloorMeshes.get(floor);
            if (!map) return;
            map.forEach((m) => {
                controlledOverlayRoot.remove(m);
                m.geometry?.dispose?.();
                m.material?.dispose?.();
            });
            map.clear();
        }

        function applyControlled(controlByFloor) {
            // 제거/갱신
            // 1) 없는 층은 클리어
            controlledFloorMeshes.forEach((_, f) => {
                if (!controlByFloor || !controlByFloor[f]) clearControlledForFloor(f);
            });
            // 2) 각 층별로 필요한 섹션만 upsert
            if (controlByFloor) {
                Object.entries(controlByFloor).forEach(([floor, ids]) => {
                    const f = normalizeFloorId(floor);
                    let map = controlledFloorMeshes.get(f);
                    if (!map) {
                        map = new Map();
                        controlledFloorMeshes.set(f, map);
                    }

                    const nextIds = new Set((ids || []).map((v) => v | 0));
                    // 제거
                    map.forEach((m, sid) => {
                        if (!nextIds.has(sid)) {
                            controlledOverlayRoot.remove(m);
                            m.geometry?.dispose?.();
                            m.material?.dispose?.();
                            map.delete(sid);
                        }
                    });
                    // 추가
                    (ids || []).forEach((sid) => {
                        sid |= 0;
                        if (map.has(sid)) return;
                        const rec = sectionById.get(sid);
                        if (!rec || normalizeFloorId(rec.floorId) !== f) return;
                        // const mesh = buildSingleSectionMesh(rec.ring);
                        const mesh = buildExtrudedControlledMesh(rec.ring, {
                            height: 0.1, // 요청한 높이
                            color: 0xef4444, // 빨강 톤
                            opacity: 0.35, // 반투명
                            curveSegments: 8,
                        });
                        if (mesh) {
                            mesh.renderOrder = 1510; // plane(1500)보다 위
                            mesh.material.depthWrite = false;
                            mesh.material.depthTest = false;
                            controlledOverlayRoot.add(mesh);
                            map.set(sid, mesh);
                        }
                    });
                });
            }
            updateEdgeVisibility && updateEdgeVisibility();
        }

        // props를 통해 제어: stateRef에 바인딩해서 외부에서 호출 가능하게
        stateRef.current.applyControlled = applyControlled;

        /* ---------------- common helpers ---------------- */
        function setGroupOpacity(group, alpha) {
            const transparent = alpha < 1.0;
            group.traverse((o) => {
                if (!o.isMesh) return;
                const mats = Array.isArray(o.material) ? o.material : [o.material];
                for (let i = 0; i < mats.length; i++) {
                    const m = mats[i];
                    if (!m) continue;
                    m.transparent = transparent;
                    m.opacity = alpha;
                    m.depthWrite = !transparent;
                    m.needsUpdate = true;
                }
            });
        }
        const applyOpacityForBuildingGroups = (alpha) => {
            floorGroups.forEach((g) => setGroupOpacity(g, alpha));
        };
        const applyOpacityForStairs = (alpha) => {
            if (stairsGroup) setGroupOpacity(stairsGroup, alpha);
        };

        function applyFloorClipToGroup(group, y0, thickness) {
            const eps = 1e-3,
                topY = y0 + thickness + eps,
                bottomY = y0 - eps;
            const planeTop = new THREE.Plane(new THREE.Vector3(0, -1, 0), topY);
            const planeBottom = new THREE.Plane(new THREE.Vector3(0, 1, 0), -bottomY);
            group.traverse((o) => {
                if (!o.isMesh || !o.material) return;
                const mats = Array.isArray(o.material) ? o.material : [o.material];
                for (let i = 0; i < mats.length; i++) {
                    const m = mats[i];
                    m.clippingPlanes = [planeTop, planeBottom];
                    m.clipIntersection = true;
                    m.clipShadows = false;
                    m.side = THREE.DoubleSide;
                    m.needsUpdate = true;
                }
            });
        }

        // 섹션 유틸
        function normalizeRing(r) {
            if (!r || r.length < 3) return r || [];
            const a = r[0],
                b = r[r.length - 1];
            return Math.abs(a.x - b.x) < 1e-6 && Math.abs(a.y - b.y) < 1e-6 ? r.slice(0, -1) : r;
        }
        function signedAreaXY(v2) {
            let s = 0;
            for (let i = 0; i < v2.length; i++) {
                const p = v2[i],
                    q = v2[(i + 1) % v2.length];
                s += p.x * q.y - q.x * p.y;
            }
            return 0.5 * s;
        }
        function buildMergedSectionsGeometryForFloor(sections, yOffset = 0.015) {
            const positions = [];
            sections.forEach((ring) => {
                const R = normalizeRing(ring);
                if (R.length < 3) return;
                const v2 = R.map((p) => new THREE.Vector2(p.x, -p.y));
                if (signedAreaXY(v2) < 0) v2.reverse();
                const tris = THREE.ShapeUtils.triangulateShape(v2, []);
                const yWorld = R[0].z + yOffset;
                for (let t = 0; t < tris.length; t++) {
                    const tri = tris[t],
                        i0 = tri[0],
                        i1 = tri[1],
                        i2 = tri[2];
                    const p0 = R[i0],
                        p1 = R[i1],
                        p2 = R[i2];
                    positions.push(p0.x, yWorld, -p0.y, p2.x, yWorld, -p2.y, p1.x, yWorld, -p1.y);
                }
            });
            if (positions.length === 0) return null;
            const geom = new THREE.BufferGeometry();
            geom.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
            geom.computeVertexNormals();
            return geom;
        }
        function floorIdForWorldY(y) {
            let best = null,
                bestDist = Infinity;
            for (const f of ALL_FLOORS) {
                const cfg = FLOOR_CLIP[f] || {};
                const meta = stateRef.current?.floorMeta?.get(f) || {};
                const y0 = Number.isFinite(cfg.y0) ? cfg.y0 : meta.y0;
                if (!Number.isFinite(y0)) continue;
                const th = Number.isFinite(cfg.thickness) ? cfg.thickness : (meta.thickness ?? 0.2);
                const tol = Math.min(Math.max(th * 0.6, 0.05), 0.35);
                const d = Math.abs(y - y0);
                if (d <= tol && d < bestDist) {
                    best = f;
                    bestDist = d;
                }
            }
            return best || null;
        }

        // 오버레이(컨투어) 메쉬 생성
        function buildOverlayPlane(y, tex) {
            const g = new THREE.PlaneGeometry(1, 1);
            g.rotateX(-Math.PI / 2);
            const A = stateRef.current?.alphaByMode?.[stateRef.current?.mode];
            const mat = new THREE.MeshBasicMaterial({
                map: tex || null,
                transparent: true,
                opacity: A?.contour ?? 1.0,
                depthWrite: false,
                depthTest: false,
            });

            const mesh = new THREE.Mesh(g, mat);
            mesh.position.set(DOMAIN.cx, y + 0.02, DOMAIN.cz);
            mesh.scale.set(overlaySize.w, 1, overlaySize.h);
            mesh.renderOrder = 1500; // 최상단
            mesh.visible = !!tex;
            return mesh;
        }

        function disposeOverlayMesh(m) {
            if (!m) return;
            m.geometry?.dispose?.();
            if (m.material) {
                m.material.map = null;
                m.material.dispose?.();
            }
            overlaysContour.remove(m);
        }
        function disposeGroupDeep(g) {
            if (!g) return;
            g.traverse((o) => {
                if (o.isMesh) {
                    o.geometry?.dispose?.();
                    if (Array.isArray(o.material)) o.material.forEach((mm) => mm?.dispose?.());
                    else o.material?.dispose?.();
                }
            });
            floorsRoot.remove(g);
        }
        function unloadAllFloors() {
            overlayPlanes.forEach((m) => disposeOverlayMesh(m));
            overlayPlanes.clear();
            floorGroups.forEach((g) => disposeGroupDeep(g));
            floorGroups.clear();
            floorMeta.clear();
            baseFloorSectionsMesh.forEach((mesh) => {
                baseSectionsRoot.remove(mesh);
                mesh.geometry?.dispose?.();
                mesh.material?.dispose?.();
            });
            baseFloorSectionsMesh.clear();
        }
        function unloadAllExcept(keepList) {
            const keep = new Set(keepList);
            overlayPlanes.forEach((m, f) => {
                if (!keep.has(f)) {
                    disposeOverlayMesh(m);
                    overlayPlanes.delete(f);
                }
            });
            floorGroups.forEach((g, f) => {
                if (!keep.has(f)) {
                    disposeGroupDeep(g);
                    floorGroups.delete(f);
                }
            });
            floorMeta.forEach((_, f) => {
                if (!keep.has(f)) floorMeta.delete(f);
            });
            baseFloorSectionsMesh.forEach((mesh, f) => {
                if (!keep.has(f)) {
                    baseSectionsRoot.remove(mesh);
                    mesh.geometry?.dispose?.();
                    mesh.material?.dispose?.();
                    baseFloorSectionsMesh.delete(f);
                }
            });
        }
        function showOnlyFloorVisible(floor) {
            floorGroups.forEach((g, key) => {
                g.visible = key === floor;
            });
            overlayPlanes.forEach((m, key) => {
                const hasTex = !!(m.material && m.material.map);
                m.visible = key === floor && hasTex;
            });
            baseFloorSectionsMesh.forEach((mesh, key) => {
                mesh.visible = key === floor;
            });
        }
        function showAllFloorsVisible() {
            floorGroups.forEach((g) => (g.visible = true));
            overlayPlanes.forEach((m) => {
                const hasTex = !!(m.material && m.material.map);
                m.visible = hasTex;
            });
            baseFloorSectionsMesh.forEach((mesh) => {
                mesh.visible = true;
            });
        }

        /* ---------------- API loads ---------------- */
        async function loadBaseSectionsMergedFromApi(url) {
            if (!url) return;
            try {
                const res = await fetch(url);
                if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
                const list = await res.json(); // [{id, shape:[PointZ...], ...}, ...]

                // 기존 메쉬 정리
                baseFloorSectionsMesh.forEach((m) => {
                    baseSectionsRoot.remove(m);
                    m.geometry?.dispose?.();
                    m.material?.dispose?.();
                });
                baseFloorSectionsMesh.clear();

                // 층별 데이터 그룹화
                const byFloor = new Map();
                (list || []).forEach((item) => {
                    if (!item) return;
                    if (typeof item === 'object' && Array.isArray(item.shape)) {
                        const ring = item.shape;
                        if (!ring || ring.length < 3) return;
                        const fid = floorIdForWorldY(ring[0].z);
                        if (Number.isFinite(item.id)) {
                            sectionById.set(item.id | 0, {
                                ring,
                                floorId: normalizeFloorId(fid),
                            });
                        }
                        if (!byFloor.has(fid)) byFloor.set(fid, []);
                        byFloor.get(fid).push(ring);
                    }
                });

                const A = stateRef.current?.alphaByMode?.[stateRef.current?.mode];

                byFloor.forEach((rings, fid) => {
                    const geom = buildMergedSectionsGeometryForFloor(rings, 0.015);
                    if (!geom) return;
                    const mat = new THREE.MeshStandardMaterial({
                        color: 0x93c5fd,
                        metalness: 0.0,
                        roughness: 0.9,
                        transparent: true,
                        opacity: A?.sections ?? 0.25,
                        depthWrite: false,
                        depthTest: false,
                    });
                    const mesh = new THREE.Mesh(geom, mat);
                    mesh.renderOrder = 850;
                    baseSectionsRoot.add(mesh);
                    baseFloorSectionsMesh.set(normalizeFloorId(fid), mesh);
                });

                updateEdgeVisibility && updateEdgeVisibility();
            } catch (e) {
                console.warn('loadBaseSectionsMergedFromApi error:', e);
            }
        }

        function loadFloor(floor, gen, visibleOnAdd) {
            return new Promise((resolve) => {
                const A = stateRef.current?.alphaByMode?.[stateRef.current?.mode];

                if (floorGroups.has(floor)) {
                    const g = floorGroups.get(floor);
                    g.visible = visibleOnAdd;
                    setGroupOpacity(g, A?.glb ?? 1.0);
                    const ov = overlayPlanes.get(floor);
                    if (ov) {
                        ov.visible = visibleOnAdd && !!(ov.material && ov.material.map);
                        const meta = floorMeta.get(floor);
                        if (meta) ov.position.y = (meta.y0 || 0) + overlayHeight + 0.02;
                        if (ov.material) ov.material.opacity = A?.contour ?? 1.0;
                    }
                    resolve(true);
                    return;
                }
                const path = glbPathForFloor(floor);
                loader.load(
                    path,
                    (gltf) => {
                        if (gen !== stateRef.current.loadGen) {
                            disposeGroupDeep(gltf.scene);
                            resolve(false);
                            return;
                        }
                        const group = gltf.scene;
                        floorsRoot.add(group);
                        floorGroups.set(floor, group);
                        overrideGroupLightGray(group);
                        const cfg = FLOOR_CLIP[floor] || {};
                        let y0 = cfg.y0,
                            thickness = cfg.thickness;
                        if (!Number.isFinite(y0) || !Number.isFinite(thickness)) {
                            const box = new THREE.Box3().setFromObject(group);
                            if (!Number.isFinite(y0)) y0 = box.min.y;
                            if (!Number.isFinite(thickness) || thickness <= 0) thickness = Math.min(0.5, (box.max.y - box.min.y) * 0.1);
                        }
                        floorMeta.set(floor, { y0, thickness });
                        applyFloorClipToGroup(group, y0, thickness);

                        setGroupOpacity(group, A?.glb ?? 1.0);

                        const tex = overlayByFloor[floor] || null;
                        const overlay = buildOverlayPlane(y0 + overlayHeight, tex);
                        overlaysContour.add(overlay);
                        overlayPlanes.set(floor, overlay);

                        group.visible = visibleOnAdd;
                        overlay.visible = visibleOnAdd && !!tex;
                        resolve(true);
                    },
                    undefined,
                    () => resolve(false)
                );
            });
        }
        async function ensureFloorsLoaded(list, gen, onlyVisibleFloor) {
            const tasks = list.map((f) => loadFloor(f, gen, onlyVisibleFloor ? f === onlyVisibleFloor : true));
            await Promise.all(tasks);
        }

        async function loadStairs(gen) {
            const A = stateRef.current?.alphaByMode?.[stateRef.current?.mode];
            if (stairsGroup) {
                setGroupOpacity(stairsGroup, A?.stairs ?? 0.9);
                return true;
            }
            return new Promise((resolve) => {
                const path = stairPath();
                loader.load(
                    path,
                    (gltf) => {
                        if (gen !== stateRef.current.loadGen) {
                            gltf.scene.traverse((o) => {
                                if (o.isMesh) {
                                    o.geometry?.dispose?.();
                                    if (Array.isArray(o.material)) o.material.forEach((mm) => mm?.dispose?.());
                                    else o.material?.dispose?.();
                                }
                            });
                            return resolve(false);
                        }
                        stairsGroup = gltf.scene;
                        overrideGroupLightGray(stairsGroup, 0x94a3b8);
                        setGroupOpacity(stairsGroup, A?.stairs ?? 0.9);
                        stairsGroup.visible = false;
                        buildingRoot.add(stairsGroup);
                        resolve(true);
                    },
                    undefined,
                    () => resolve(false)
                );
            });
        }
        function showStairsForFloor(floorId) {
            const A = stateRef.current?.alphaByMode?.[stateRef.current?.mode];
            if (!stairsGroup) return;
            const meta = floorMeta.get(floorId);
            if (!meta) {
                stairsGroup.visible = false;
                return;
            }
            const margin = 0.4,
                y0 = (meta.y0 ?? 0) - margin;
            const thickness = Math.max((meta.thickness ?? 0.2) + margin * 2, (meta.thickness ?? 0.2) * 2);
            applyFloorClipToGroup(stairsGroup, y0, thickness);
            setGroupOpacity(stairsGroup, A?.stairs ?? 0.9);
            stairsGroup.visible = true;
        }
        function hideStairs() {
            if (stairsGroup) stairsGroup.visible = false;
        }

        /* ---------------- routes build ---------------- */
        // === Route selection state (경로 강조용) ======================
        const routeMeshesByKey = new Map(); // routeKey -> { mesh, floorId }
        const routeMeshToKey = new Map(); // mesh.uuid -> routeKey
        const routeOriginByKey = new Map(); // routeKey -> originFloor (출발 층)

        function applyRouteSelectionStyle() {
            const s = stateRef.current;
            if (!s || !s.routeMeshesByKey) return;

            const mode = s.mode || 'building';
            const selectedKey = s.selectedRouteKey;
            const selFloor = normalizeFloorId(s.selectedFloor || '');
            const A = s.alphaByMode?.[mode]?.routes ?? 1.0;
            const brMode = s?.routeViewMode ?? routeViewMode; // "all" | "fromSelectedFloor"

            s.routeMeshesByKey.forEach(({ mesh, floorId }, key) => {
                const mat = mesh?.material;
                if (!mat) return;

                // 항상 기본색으로 복원
                const baseColor = mat.userData?.baseColor;
                if (baseColor) {
                    mat.color.copy(baseColor);
                }

                // 출발층 정보 (없으면 floorId 를 fallback 으로 사용)
                const originFloorRaw = s.routeOriginByKey?.get(key);
                const originFloorNorm = originFloorRaw ? normalizeFloorId(originFloorRaw) : normalizeFloorId(floorId);

                const isOnSelectedFloor = normalizeFloorId(floorId) === selFloor;
                const isFromSelectedFloor = selFloor && originFloorNorm === selFloor;
                const isSelectedRoute = !!selectedKey && key === selectedKey;
                const hasSelection = !!selectedKey;

                let width = 1;
                let opacity = A;

                // ---------- 메인 라인 두께/투명도 ----------
                if (mode === 'floor' || brMode === 'fromSelectedFloor') {
                    // 층뷰: 전체 경로는 다 보이되, 두께는 동일
                    width = 5;
                    opacity = A;
                } else {
                    // 건물뷰: 선택된 경로만 두껍게, 나머지는 희미하게
                    if (!hasSelection) {
                        width = 1;
                    } else {
                        if (isSelectedRoute) {
                            width = 5;
                            opacity = A;
                        } else {
                            width = 1;
                            opacity = A * 0;
                        }
                    }
                }

                mat.linewidth = width;
                mat.transparent = opacity < 1.0;
                mat.opacity = opacity;
                mat.needsUpdate = true;

                // ---------- 출발/도착 마커 투명도/표시 ----------
                const markers = s.routeMarkersByKey?.get(key);
                if (markers) {
                    // 라인의 visible 상태 + 대피경로 on/off 에 맞춰서 마커도 보이게
                    const showR = !!(s.showRoutes ?? true);
                    const markerVisible = !!mesh.visible && showR;

                    // 선택된 경로만 진하게, 나머지는 살짝 흐리게
                    const targetOpacity = !selectedKey || key === selectedKey ? 1.0 : 0.3;

                    [markers.startMarker, markers.endMarker].forEach((m) => {
                        if (!m || !m.material) return;
                        m.visible = markerVisible;
                        if (markerVisible) {
                            m.material.opacity = targetOpacity;
                            m.material.needsUpdate = true;
                        }
                    });
                }

                // ---------- 1px 하이라이트 라인 ----------
                const hi = mesh.userData?.highlight;
                if (hi && hi.material) {
                    const hm = hi.material;

                    let showHighlight = false;

                    // 건물뷰: 선택된 경로만 1px 하이라이트
                    showHighlight = isSelectedRoute;

                    hi.visible = showHighlight;
                    hm.opacity = showHighlight ? 1.0 : 0.0;
                    hm.transparent = hm.opacity < 1.0;
                    hm.needsUpdate = true;
                }
            });
        }

        function clearRouteSelection() {
            const s = stateRef.current;
            if (!s) return;
            s.selectedRouteKey = null;
            applyRouteSelectionStyle();
        }

        function selectRoute(routeKey) {
            const s = stateRef.current;
            if (!s) return;
            s.selectedRouteKey = routeKey;
            applyRouteSelectionStyle();
        }

        function buildFloorEdges(floor, floorObj, { color = 0xff0000, linewidth = 1 }) {
            try {
                const pos = floorObj.positions || [],
                    seg = floorObj.segments || [];
                if (!pos.length || !seg.length) return null;

                const segCount = (seg.length / 2) | 0;
                const outPos = new Float32Array(segCount * 2 * 3);
                const toWorld = (x, y, z) => [x, z ?? 0, -y];

                for (let sIdx = 0; sIdx < segCount; sIdx++) {
                    const i = seg[2 * sIdx] | 0,
                        j = seg[2 * sIdx + 1] | 0;
                    const ax = pos[3 * i],
                        ay = pos[3 * i + 1],
                        az = pos[3 * i + 2];
                    const bx = pos[3 * j],
                        by = pos[3 * j + 1],
                        bz = pos[3 * j + 2];
                    const [Ax, Ay, Az] = toWorld(ax, ay, az);
                    const [Bx, By, Bz] = toWorld(bx, by, bz);
                    const base = sIdx * 2 * 3;
                    outPos[base + 0] = Ax;
                    outPos[base + 1] = Ay;
                    outPos[base + 2] = Az;
                    outPos[base + 3] = Bx;
                    outPos[base + 4] = By;
                    outPos[base + 5] = Bz;
                }

                const g = new LineSegmentsGeometry();
                g.setPositions(outPos);

                const A = stateRef.current?.alphaByMode?.[stateRef.current?.mode];

                // 굵은 메인 라인(초록)
                const m = new LineMaterial({
                    color,
                    linewidth,
                    worldUnits: false,
                    transparent: true,
                    opacity: A?.routes ?? 1.0,
                    depthTest: false,
                });
                m.depthWrite = false;
                m.toneMapped = false;
                m.vertexColors = false;

                // ⭐ 기본색 저장(선택/해제 시 복원용)
                m.userData.baseColor = new THREE.Color(color);

                const size = new THREE.Vector2();
                renderer.getSize(size);
                m.resolution.set(size.x, size.y);

                const mesh = new LineSegments2(g, m);
                mesh.name = `edges:${floor}`;

                // ───────────────────── 하이라이트 1px 라인 ─────────────────────
                const hiMat = new LineMaterial({
                    color: 0xff0000, // 강조 색
                    linewidth: 1, // 1px
                    worldUnits: false,
                    transparent: true,
                    opacity: 0.0, // 기본은 숨김
                    depthTest: false,
                });
                hiMat.depthWrite = false;
                hiMat.toneMapped = false;
                hiMat.vertexColors = false;
                hiMat.resolution.set(size.x, size.y);

                // 같은 geometry 를 공유하는 얇은 라인
                const hiLine = new LineSegments2(g, hiMat);

                // 확실하게 최상단에 그리도록 큰 값으로 설정
                hiLine.renderOrder = 2000;
                hiLine.frustumCulled = false; // 잘리지 않게

                // 메쉬의 자식으로 붙이면 변환은 같이 받으면서, renderOrder 는 hiLine 의 값 사용
                mesh.add(hiLine);

                // 선택/층뷰 스타일에서 접근할 수 있도록 저장
                mesh.userData.highlight = hiLine;
                // ────────────────────────────────────────────────

                return mesh;
            } catch (e) {
                console.warn('buildFloorEdges(fat) fail:', e);
                return null;
            }
        }

        async function loadBaseEdgesFromApi(url) {
            if (!url) return;
            try {
                const res = await fetch(url);
                if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
                const json = await res.json();
                const floorsObj = json.floors || {};
                const order = Array.isArray(json.order) ? json.order : Object.keys(floorsObj);
                baseFloorEdges.forEach((mesh) => {
                    baseEdgesRoot.remove(mesh);
                    mesh.traverse((o) => {
                        if (!o.isMesh) return;
                        o.geometry?.dispose?.();
                        if (Array.isArray(o.material)) o.material.forEach((mm) => mm?.dispose?.());
                        else o.material?.dispose?.();
                    });
                });
                baseFloorEdges.clear();
                order.forEach((floor) => {
                    const mesh = buildFloorEdges(floor, floorsObj[floor]);
                    if (mesh) {
                        baseEdgesRoot.add(mesh);
                        baseFloorEdges.set(floor, mesh);
                    }
                });
                updateEdgeVisibility();
            } catch (e) {
                console.warn('loadBaseEdgesFromApi error:', e);
            }
        }

        /* ---------------- plan view switchers ---------------- */
        function makeOrthoCamera(frustumHeight) {
            const W = mount.clientWidth || 1,
                H = mount.clientHeight || 1;
            const aspect = W / Math.max(1, H);
            const top = frustumHeight / 2;
            const bottom = -top;
            const right = top * aspect;
            const left = -right;
            const cam = new THREE.OrthographicCamera(left, right, top, bottom, 0.1, 10000);
            cam.zoom = 1.0;
            cam.up.set(0, 0, -1);
            cam.updateProjectionMatrix();
            return cam;
        }
        function updateOrthoFrustum(cam) {
            const s = stateRef.current;
            if (!cam?.isOrthographicCamera || !s) return;
            const W = mount.clientWidth || 1,
                H = mount.clientHeight || 1;
            const aspect = W / Math.max(1, H);
            const frustumHeight = s.planFrustumSize || Math.max(overlaySize.h, overlaySize.w) * 1.2;
            const top = frustumHeight / 2,
                bottom = -top;
            const right = top * aspect,
                left = -right;
            cam.left = left;
            cam.right = right;
            cam.top = top;
            cam.bottom = bottom;
            cam.updateProjectionMatrix();
        }

        // 초기 Plan 프러스텀 크기(층 전체가 들어오도록 여유)
        stateRef.current.planFrustumSize = Math.max(overlaySize.h, overlaySize.w) * 1.35;

        function attachControls(cam, rotateEnabled) {
            const c = new OrbitControls(cam, renderer.domElement);
            c.enableDamping = true;
            c.dampingFactor = 0.08;
            c.zoomSpeed = 0.9;
            c.enableRotate = !!rotateEnabled;
            if (!rotateEnabled) {
                // 평면모드: 회전 완전 금지, 화면 기준 팬/줌만
                c.enablePan = true;
                c.enableZoom = true;
                c.enableRotate = false;
                c.screenSpacePanning = true; // 화면 X/Y로 바로 팬
                // 마우스/터치 맵도 팬 중심으로
                c.mouseButtons = {
                    LEFT: THREE.MOUSE.PAN,
                    MIDDLE: THREE.MOUSE.DOLLY,
                    RIGHT: THREE.MOUSE.PAN,
                };
                c.touches = { ONE: THREE.TOUCH.PAN, TWO: THREE.TOUCH.DOLLY_PAN };
            }
            // 공통 이벤트
            c.addEventListener('change', updateHUD);
            c.addEventListener('change', onControlsChange);
            return c;
        }

        function detachControls() {
            const s = stateRef.current;
            if (!s?.controls) return;
            s.controls.removeEventListener('change', updateHUD);
            s.controls.removeEventListener('change', onControlsChange);
            s.controls.dispose?.();
        }

        stateRef.current.switchToPlan = () => {
            const s = stateRef.current;
            if (!s) return;

            // 기존 3D 뷰 저장
            s.viewSnapshot3D = snapshotView(s.camera, s.controls);

            // Ortho 카메라 생성/업데이트
            const ortho = s.orthoCam || makeOrthoCamera(s.planFrustumSize);
            s.orthoCam = ortho;
            updateOrthoFrustum(ortho);

            // 타겟은 현재 층 중앙 평면
            const sel = s.selectedFloor || 'B1';
            const meta = s.floorMeta?.get(sel) || {};
            const yPlane = (Number.isFinite(meta.y0) ? meta.y0 : 0) + overlayHeight + 0.02;

            const target = new THREE.Vector3(DOMAIN.cx, yPlane, DOMAIN.cz);
            ortho.position.set(target.x, yPlane + 500, target.z);
            ortho.lookAt(target);

            detachControls();
            const c = attachControls(ortho, /*rotateEnabled=*/ false);
            c.target.copy(target);
            c.update();

            // 활성 카메라 교체
            s.camera = ortho;
            s.controls = c;
            updateHUD();
        };

        stateRef.current.switchToPerspective = () => {
            const s = stateRef.current;
            if (!s) return;

            // 기존 퍼스펙티브가 없으면 초기값 사용
            const cam = s.perspCam || perspCam;
            s.perspCam = cam;

            const prevView = snapshotView(s.camera, s.controls);

            detachControls();
            const c = attachControls(cam, /*rotateEnabled=*/ true);
            // 저장된 3D 뷰 복원(있으면)
            if (s.viewSnapshot3D) {
                cam.position.copy(s.viewSnapshot3D.pos);
                c.target.copy(s.viewSnapshot3D.target);
            } else if (prevView) {
                cam.position.copy(prevView.pos);
                c.target.copy(prevView.target);
            } else if (Array.isArray(fixedInitialView?.pos) && Array.isArray(fixedInitialView?.target)) {
                cam.position.set(...fixedInitialView.pos);
                c.target.set(...fixedInitialView.target);
            } else {
                // 완전 마지막 안전장치: 초기 타깃/포지션
                cam.position.set(DOMAIN.cx + 80, 160, DOMAIN.cz + 120);
                c.target.set(DOMAIN.cx, 20, DOMAIN.cz);
            }
            c.update();

            s.camera = cam;
            s.controls = c;
            updateHUD();
        };

        /* ---------------- pointer picking (카메라 참조 수정) ---------------- */
        const raycaster = new THREE.Raycaster(),
            pointer = new THREE.Vector2();
        raycaster.params.Line = {
            threshold: 1.0,
        };

        // 화면 좌표 기준으로 "가장 가까운 경로"를 찾는 헬퍼
        const _v0 = new THREE.Vector3();
        const _v1 = new THREE.Vector3();
        const _p0 = new THREE.Vector2();
        const _p1 = new THREE.Vector2();

        function pointToSegmentDistSq(px, py, p0, p1) {
            const vx = p1.x - p0.x;
            const vy = p1.y - p0.y;
            const wx = px - p0.x;
            const wy = py - p0.y;

            const c1 = vx * wx + vy * wy;
            if (c1 <= 0) {
                return wx * wx + wy * wy;
            }
            const c2 = vx * vx + vy * vy;
            if (c2 <= c1) {
                const dx = px - p1.x;
                const dy = py - p1.y;
                return dx * dx + dy * dy;
            }
            const t = c1 / c2;
            const projx = p0.x + t * vx;
            const projy = p0.y + t * vy;
            const dx = px - projx;
            const dy = py - projy;
            return dx * dx + dy * dy;
        }

        function pickRouteByScreenDistance(ev, s, rect) {
            if (!s || !s.routeMeshesByKey || !s.camera) return null;

            const cam = s.camera;
            const mouseX = ev.clientX - rect.left;
            const mouseY = ev.clientY - rect.top;

            let bestKey = null;
            let bestDistSq = Infinity;

            // routeMeshesByKey: routeKey -> { mesh, floorId }
            s.routeMeshesByKey.forEach(({ mesh }, routeKey) => {
                if (!mesh || !mesh.visible) return;

                const geom = mesh.geometry;
                if (!geom) return;

                // LineSegmentsGeometry 는 instanceStart / instanceEnd 를 사용
                const startAttr = geom.attributes.instanceStart;
                const endAttr = geom.attributes.instanceEnd;
                if (!startAttr || !endAttr) return;

                const segCount = startAttr.count;
                for (let i = 0; i < segCount; i++) {
                    _v0.fromBufferAttribute(startAttr, i);
                    _v1.fromBufferAttribute(endAttr, i);

                    // 월드 → NDC
                    _v0.project(cam);
                    _v1.project(cam);

                    // NDC(-1~1) → 화면(px)
                    _p0.set((_v0.x * 0.5 + 0.5) * rect.width, (-_v0.y * 0.5 + 0.5) * rect.height);
                    _p1.set((_v1.x * 0.5 + 0.5) * rect.width, (-_v1.y * 0.5 + 0.5) * rect.height);

                    const d2 = pointToSegmentDistSq(mouseX, mouseY, _p0, _p1);
                    if (d2 < bestDistSq) {
                        bestDistSq = d2;
                        bestKey = routeKey;
                    }
                }
            });

            const THRESH_PX = 12; // 마우스에서 12px 안쪽이면 클릭으로 인정
            if (bestKey && bestDistSq <= THRESH_PX * THRESH_PX) {
                return bestKey;
            }
            return null;
        }
        const pickMarker = new THREE.Mesh(
            new THREE.SphereGeometry(0.25, 16, 16),
            new THREE.MeshBasicMaterial({
                color: 0xff0000,
                transparent: true,
                opacity: 1,
                depthTest: false,
                depthWrite: false,
            })
        );
        pickMarker.visible = false;
        pickMarker.renderOrder = 2000;
        overlayScene.add(pickMarker);

        const clickStart = { x: 0, y: 0, t: 0 };
        const CLICK_MOVE_EPS = 6,
            CLICK_TIME_EPS = 500,
            CAM_EPS = 1e-3;
        let downView = null;
        function handlePointerDown(ev) {
            const s = stateRef.current;
            if (!s) return;
            clickStart.x = ev.clientX;
            clickStart.y = ev.clientY;
            clickStart.t = performance.now();
            hadControlChange = false;
            downView = {
                pos: s.camera.position.clone(),
                target: s.controls.target.clone(),
            };
        }
        function handlePointerUp(ev) {
            const s = stateRef.current;
            if (!s) return;
            if (ev.button !== 0) return;

            const dx = ev.clientX - clickStart.x;
            const dy = ev.clientY - clickStart.y;
            const dt = performance.now() - clickStart.t;
            if (Math.hypot(dx, dy) > CLICK_MOVE_EPS || dt > CLICK_TIME_EPS) return;

            const camMoved = !downView || s.camera.position.distanceTo(downView.pos) > CAM_EPS || s.controls.target.distanceTo(downView.target) > CAM_EPS || hadControlChange;
            if (camMoved) return;

            const curMode = s.mode ?? mode;
            const curFloor = s.selectedFloor ?? selectedFloor;

            const rect = renderer.domElement.getBoundingClientRect();
            pointer.x = ((ev.clientX - rect.left) / rect.width) * 2 - 1;
            pointer.y = -((ev.clientY - rect.top) / rect.height) * 2 + 1;
            raycaster.setFromCamera(pointer, s.camera);

            // [1] 모드 공통: 먼저 경로 픽 시도
            const pickedKey = pickRouteByScreenDistance(ev, s, rect);
            if (pickedKey) {
                s.selectRoute?.(pickedKey);
                if (onRouteSelected) {
                    onRouteSelected(pickedKey);
                }
                return; // 경로를 선택했으면 여기서 끝
            }

            // [2] building 모드: 배경 클릭 시 선택 해제만
            if (s.selectedRouteKey) {
                s.clearRouteSelection?.();

                if (onRouteCleared) onRouteCleared();
                return;
            }

            // [3] floor 모드: 경로를 못 골랐을 때만 cell pick 로직 수행
            const simId = s.simulationId;
            if (curMode !== 'floor') return;
            if (!simId || !send) return;

            const ov = overlayPlanes.get(curFloor);
            let hitPoint = null;

            // 컨투어 오버레이 plane 에서만 픽킹 허용
            if (ov && ov.visible && ov.material && ov.material.map) {
                const hits = raycaster.intersectObject(ov, false);
                if (hits && hits.length > 0) {
                    hitPoint = hits[0].point;
                }
            }

            // 오버레이 영역을 안 찍었으면 픽킹하지 않음
            if (!hitPoint) return;

            const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
            const wx = hitPoint.x;
            const wz = hitPoint.z;
            const simX = clamp(wx, DOMAIN.minX, DOMAIN.maxX);
            const simY = clamp(-wz, 0, Math.abs(DOMAIN.minZ));

            pickMarker.position.set(simX, hitPoint.y + 0.1, -simY);
            pickMarker.visible = true;
            try {
                send({ action: 'set_pick', simulationId: simId, x: simX, y: simY });
            } catch {}
        }

        renderer.domElement.addEventListener('pointerdown', handlePointerDown);
        renderer.domElement.addEventListener('pointerup', handlePointerUp);

        /* ---------------- render loop (활성 카메라 사용) ---------------- */
        let running = true;
        (function loop() {
            if (!running) return;
            const s = stateRef.current;
            s?.controls?.update();
            const cam = s?.camera || perspCam;
            renderer.autoClear = true;
            renderer.render(scene, cam);
            renderer.autoClear = false;
            renderer.clearDepth();
            renderer.render(overlayScene, cam);
            requestAnimationFrame(loop);
        })();

        // === MiniMaps init ===
        const initMini = () => {
            const topEl = miniTopRef.current,
                botEl = miniBottomRef.current;
            if (topEl && !miniStateRef.current.top.renderer) miniStateRef.current.top = makeMiniScene(topEl);
            if (botEl && !miniStateRef.current.bottom.renderer) miniStateRef.current.bottom = makeMiniScene(botEl);
            renderMiniAll();
        };
        const renderMiniAll = () => {
            const meta = floorMeta.get(stateRef.current.selectedFloor || 'B1') || {};
            const y0 = Number.isFinite(meta.y0) ? meta.y0 : 0;
            ['top', 'bottom'].forEach((k) => {
                const M = miniStateRef.current[k];
                if (!M?.renderer) return;
                M.plane.position.y = y0 + overlayHeight + 0.02;
                M.renderer.autoClear = true;
                M.renderer.render(M.scene, M.cam);
            });
        };
        initMini();

        /* ---------------- resize (활성 카메라 분기) ---------------- */
        let resizeRaf = 0;
        const ro = new ResizeObserver(() => {
            if (resizeRaf) cancelAnimationFrame(resizeRaf);
            resizeRaf = requestAnimationFrame(() => {
                const s = stateRef.current;
                const W2 = mount.clientWidth || 1,
                    H2 = mount.clientHeight || 1;
                renderer.setSize(W2, H2, false);
                const cam = s?.camera || perspCam;
                if (cam.isPerspectiveCamera) {
                    cam.aspect = W2 / Math.max(1, H2);
                    cam.updateProjectionMatrix();
                } else if (cam.isOrthographicCamera) {
                    updateOrthoFrustum(cam);
                }
                // 라인 해상도 업데이트
                const updateLineResolution = (mesh) => {
                    const mat = mesh.material;
                    if (mat && mat.resolution) {
                        mat.resolution.set(W2, H2);
                    }
                    const hi = mesh.userData?.highlight;
                    if (hi && hi.material && hi.material.resolution) {
                        hi.material.resolution.set(W2, H2);
                    }
                };

                floorEdges.forEach((val) => {
                    (Array.isArray(val) ? val : [val]).forEach(updateLineResolution);
                });
                baseFloorEdges.forEach(updateLineResolution);

                // 미니맵 캔버스 크기 재설정
                ['top', 'bottom'].forEach((k) => {
                    const el = k === 'top' ? miniTopRef.current : miniBottomRef.current;
                    const M = miniStateRef.current[k];
                    if (el && M?.renderer) {
                        const w = el.clientWidth || 1,
                            h = el.clientHeight || 1;
                        M.renderer.setSize(w, h, false);
                        const aspect = w / Math.max(1, h);
                        const frustumHeight = Math.max(overlaySize.h, overlaySize.w) * 1.2;
                        const top = frustumHeight / 2,
                            bottom = -top;
                        const right = top * aspect,
                            left = -right;
                        M.cam.left = left;
                        M.cam.right = right;
                        M.cam.top = top;
                        M.cam.bottom = bottom;
                        M.cam.updateProjectionMatrix();
                    }
                });
                renderMiniAll();
            });
        });
        ro.observe(mount);

        /* ---------------- stateRef exposure ---------------- */
        stateRef.current = {
            ...stateRef.current,
            scene,
            renderer,
            buildingRoot,
            floorsRoot,
            overlaysRoot: overlaysContour,
            routeMarkersRoot,
            floorGroups,
            overlayPlanes,
            floorMeta,
            unloadAllFloors,
            unloadAllExcept,
            ensureFloorsLoaded,
            showOnlyFloorVisible,
            showAllFloorsVisible,
            setGroupOpacity,
            applyOpacityForBuildingGroups,
            applyOpacityForStairs,
            loadGen: stateRef.current.loadGen,
            mode: initialMode,
            selectedFloor,
            pickMarker,
            simulationId,
            send,
            edgesRoot,
            floorEdges,
            baseEdgesRoot,
            baseFloorEdges,
            baseSectionsRoot,
            baseFloorSectionsMesh,
            loadBaseEdgesFromApi,
            updateEdgeVisibility,
            updateRouteThickness: () => {
                const s2 = stateRef.current;
                if (!s2) return;
                s2.applyRouteSelectionStyle?.();
            },
            showRoutes: ROUTE_VIEW_INIT,
            showBaseSections: true,
            buildFloorEdges,
            loadStairs,
            showStairsForFloor,
            hideStairs,
            loadBaseSectionsMergedFromApi,

            // cameras / controls
            camera: stateRef.current.camera,
            controls: stateRef.current.controls,
            perspCam,
            orthoCam: null,
            planFrustumSize: stateRef.current.planFrustumSize,
            switchToPlan: stateRef.current.switchToPlan,
            switchToPerspective: stateRef.current.switchToPerspective,

            routeMeshesByKey,
            routeMeshToKey,
            routeOriginByKey,
            selectedRouteKey: null,
            selectRoute,
            clearRouteSelection,
            applyRouteSelectionStyle,
            routeViewMode, // "all" | "fromSelectedFloor"
            contourFloorModeEnabled,

            // 0~1 모드별 알파 초기 저장
            alphaByMode: {
                building: {
                    glb: alphaUiByMode.building.glb / 100,
                    stairs: alphaUiByMode.building.stairs / 100,
                    sections: alphaUiByMode.building.sections / 100,
                    routes: alphaUiByMode.building.routes / 100,
                    contour: alphaUiByMode.building.contour / 100,
                },
                floor: {
                    glb: alphaUiByMode.floor.glb / 100,
                    stairs: alphaUiByMode.floor.stairs / 100,
                    sections: alphaUiByMode.floor.sections / 100,
                    routes: alphaUiByMode.floor.routes / 100,
                    contour: alphaUiByMode.floor.contour / 100,
                },
            },
        };

        /* ---------------- cleanup ---------------- */
        return () => {
            const s = stateRef.current;
            s?.controls?.removeEventListener('change', updateHUD);
            s?.controls?.removeEventListener('change', onControlsChange);
            running = false;
            ro.disconnect();
            renderer.domElement.removeEventListener('pointerdown', handlePointerDown);
            renderer.domElement.removeEventListener('pointerup', handlePointerUp);

            if (gridLines) {
                gridLines.geometry?.dispose?.();
                gridLines.material?.dispose?.();
                scene.remove(gridLines);
            }
            overlayPlanes.forEach((m) => {
                m.geometry?.dispose?.();
                if (m.material) {
                    m.material.map = null;
                    m.material.dispose?.();
                }
                overlaysContour.remove(m);
            });
            floorGroups.forEach((g) => disposeGroupDeep(g));
            baseFloorSectionsMesh.forEach((mesh) => {
                baseSectionsRoot.remove(mesh);
                mesh.geometry?.dispose?.();
                mesh.material?.dispose?.();
            });
            baseFloorSectionsMesh.clear();

            if (s?.pickMarker) {
                overlayScene.remove(s.pickMarker);
                s.pickMarker.geometry?.dispose?.();
                s.pickMarker.material?.dispose?.();
            }
            mount.removeChild(renderer.domElement);
            renderer.dispose();

            // 미니맵 정리
            ['top', 'bottom'].forEach((k) => {
                const M = miniStateRef.current[k];
                if (!M) return;
                M.plane?.geometry?.dispose?.();
                if (M.plane?.material) {
                    M.plane.material.map = null;
                    M.plane.material.dispose?.();
                }
                M.grid?.geometry?.dispose?.();
                M.grid?.material?.dispose?.();
                M.renderer?.dispose?.();
                miniStateRef.current[k] = {
                    renderer: null,
                    scene: null,
                    cam: null,
                    plane: null,
                    grid: null,
                };
            });
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // mount

    /* ---------------- routes payload -> edges ---------------- */
    function worldFromIndex(posArr, idx) {
        const x = posArr[3 * idx];
        const y = posArr[3 * idx + 1];
        const z = posArr[3 * idx + 2];
        // buildFloorEdges 와 동일한 좌표 변환: (x, y, z) -> (x, z, -y)
        return new THREE.Vector3(x, z ?? 0, -y);
    }

    function createEndpointMarker(worldPos, { isStart }) {
        const mesh = isStart ? makeStartMarkerMesh() : makeEndMarkerMesh();

        // 바닥에서 살짝 띄우기
        const lifted = worldPos.clone();
        lifted.y += 0.4;
        mesh.position.copy(lifted);
        return mesh;
    }

    useEffect(() => {
        const s = stateRef.current;
        if (!s || !routesPayload || !s.buildFloorEdges) return;

        const floorsObj = routesPayload.floors || {};

        // routesPayload 안의 originMap 가져오기 (서버 구조에 따라 둘 다 시도)
        const originMap = routesPayload.routes?.originMap || routesPayload.originMap || {};

        // 기존 라인/맵 정리
        s.floorEdges.forEach((val) => {
            (Array.isArray(val) ? val : [val]).forEach((mesh) => {
                s.edgesRoot.remove(mesh);
                mesh.traverse((o) => {
                    if (!o.isMesh) return;
                    if (o.geometry) o.geometry.dispose?.();
                    if (o.material) {
                        if (Array.isArray(o.material)) o.material.forEach((mm) => mm?.dispose?.());
                        else o.material.dispose?.();
                    }
                });
            });
        });
        s.floorEdges.clear();
        s.routeMeshesByKey?.clear?.();
        s.routeMeshToKey?.clear?.();
        s.routeOriginByKey?.clear?.(); // 출발층 정보도 초기화

        // 기존 start/end 마커 정리
        if (s.routeMarkersRoot) {
            const children = [...s.routeMarkersRoot.children];
            children.forEach((m) => {
                s.routeMarkersRoot.remove(m);
                m.geometry?.dispose?.();
                m.material?.dispose?.();
            });
        }
        s.routeMarkersByKey = new Map(); // routeKey -> { startMarker, endMarker }

        // originMap에서 routeKey -> originFloor 추출 헬퍼
        const getOriginFloorFor = (routeKey, rawKey, rawRouteId) => {
            if (!originMap) return null;

            const candidates = [routeKey, rawKey, rawRouteId != null ? String(rawRouteId) : null].filter(Boolean);

            for (const c of candidates) {
                const v = originMap[c];
                if (!v) continue;

                let floorStr = null;

                // 1) "b1" / "b1:xxx" / "B1" 같은 문자열인 경우
                if (typeof v === 'string') {
                    const part = v.split(':')[0];
                    floorStr = part;
                }
                // 2) { floor: "B1", ... } or { floorId: "b1" } 같은 객체인 경우
                else if (typeof v === 'object') {
                    const cand = v.floor ?? v.floorId ?? v.f;
                    if (typeof cand === 'string') floorStr = cand;
                }

                if (floorStr) {
                    return normalizeFloorId(floorStr);
                }
            }
            return null;
        };

        // 층별로 라인 묶기: floorId -> [mesh...]
        const mergedByFloor = new Map();

        const registerRouteMesh = (routeKey, floorId, mesh) => {
            if (!s.routeMeshesByKey || !s.routeMeshToKey) return;
            s.routeMeshesByKey.set(routeKey, { mesh, floorId });
            s.routeMeshToKey.set(mesh.uuid, routeKey);
        };

        // 실제 라인 + 마커를 만드는 함수
        const buildRouteWithMarkers = (floorId, routeObj, routeKey, rawKey, rawRouteId) => {
            const color = 0x22c55e;
            const mesh = s.buildFloorEdges(floorId, routeObj, {
                color,
                linewidth: 2,
            });
            if (!mesh) return;

            s.edgesRoot.add(mesh);

            let list = mergedByFloor.get(floorId);
            if (!list) {
                list = [];
                mergedByFloor.set(floorId, list);
            }
            list.push(mesh);

            registerRouteMesh(routeKey, floorId, mesh);

            // start/end 좌표 계산 → 마커 생성
            const pos = routeObj.positions || [];
            const seg = routeObj.segments || [];
            if (pos.length >= 3 && seg.length >= 2) {
                const firstIdx = seg[0] | 0;
                const lastIdx = seg[seg.length - 1] | 0;

                const startPos = worldFromIndex(pos, firstIdx);
                const endPos = worldFromIndex(pos, lastIdx);

                const startMarker = createEndpointMarker(startPos, { isStart: true });
                const endMarker = createEndpointMarker(endPos, { isStart: false });

                if (s.routeMarkersRoot) {
                    s.routeMarkersRoot.add(startMarker);
                    s.routeMarkersRoot.add(endMarker);
                }

                s.routeMarkersByKey.set(routeKey, { startMarker, endMarker });
            }

            // 출발 층 저장
            const originFloor = getOriginFloorFor(routeKey, rawKey, rawRouteId);
            if (originFloor) {
                s.routeOriginByKey.set(routeKey, originFloor);
            }
        };

        Object.entries(floorsObj).forEach(([rawKey, payload]) => {
            const [rawFloor, rawRouteId] = String(rawKey).split(':');
            const floorId = normalizeFloorId(rawFloor);
            if (!floorId) return;

            const makeRouteKey = (routeObj, idx) => {
                if (routeObj && routeObj.id != null) return String(routeObj.id);
                if (rawRouteId != null) return String(rawRouteId);
                return `${rawKey}:${idx}`;
            };

            if (Array.isArray(payload)) {
                payload.forEach((routeObj, i) => {
                    const routeKey = makeRouteKey(routeObj, i);
                    buildRouteWithMarkers(floorId, routeObj, routeKey, rawKey, rawRouteId);
                });
            } else if (payload) {
                const routeKey = makeRouteKey(payload, 0);
                buildRouteWithMarkers(floorId, payload, routeKey, rawKey, rawRouteId);
            }
        });

        mergedByFloor.forEach((meshes, floorId) => {
            if (meshes.length) {
                s.floorEdges.set(floorId, meshes);
            }
        });

        // 3) 가시성/선택 상태 복원
        const prevSelected = s.selectedRouteKey || null;
        s.updateEdgeVisibility && s.updateEdgeVisibility();

        if (prevSelected && s.routeMeshesByKey.has(prevSelected)) {
            s.selectedRouteKey = prevSelected;
            if (onRouteSelected) {
                onRouteSelected(prevSelected);
            }
        } else {
            if (prevSelected && onRouteCleared) {
                onRouteCleared();
            }
            s.selectedRouteKey = null;
        }
        s.applyRouteSelectionStyle?.();
    }, [routesPayload]);

    // 통제구역 반영
    useEffect(() => {
        stateRef.current?.applyControlled?.(controlledByFloor || {});
    }, [controlledByFloor]);

    /* ---------------- external props sync ---------------- */
    useEffect(() => {
        const s = stateRef.current;
        if (!s) return;
        s.simulationId = simulationId;
        s.send = send;
    }, [simulationId, send]);

    /* ---------------- mode change ---------------- */
    useEffect(() => {
        const s = stateRef.current;
        if (!s || !s.ensureFloorsLoaded) return;
        s.mode = mode;

        // 건물뷰로 전환하면 평면모드 강제 해제 + 퍼스펙티브 복귀
        if (mode !== 'floor' && planMode) setPlanMode(false);

        (async () => {
            if (mode !== 'floor' && !s.camera?.isPerspectiveCamera) {
                s.switchToPerspective?.();
            }
            const snap = snapshotView(s.camera, s.controls);
            s.loadGen = (s.loadGen || 0) + 1;
            const gen = s.loadGen;

            if (mode === 'building') {
                await s.ensureFloorsLoaded(ALL_FLOORS, gen, null);
                s.applyOpacityForBuildingGroups(s.alphaByMode?.[mode]?.glb ?? 1.0);
                s.applyOpacityForStairs(s.alphaByMode?.[mode]?.stairs ?? 0.9);
                s.showAllFloorsVisible();
            } else {
                const curFloor = s.selectedFloor || 'B1';
                s.unloadAllExcept([curFloor]);
                s.showOnlyFloorVisible(curFloor);
                await s.ensureFloorsLoaded([curFloor], gen, curFloor);
                s.applyOpacityForBuildingGroups(s.alphaByMode?.[mode]?.glb ?? 0.9);
                s.applyOpacityForStairs(s.alphaByMode?.[mode]?.stairs ?? 0.9);
                s.showOnlyFloorVisible(curFloor);
            }

            await s.loadStairs?.(s.loadGen);
            s.showStairsForFloor?.(s.selectedFloor || 'B1');

            if (s.baseFloorSectionsMesh && s.baseFloorSectionsMesh.size === 0) {
                await s.loadBaseSectionsMergedFromApi?.('/api/simulation/basesections');
            }

            restoreView(s.camera, s.controls, snap);
        })();
        s.updateEdgeVisibility && s.updateEdgeVisibility();
    }, [mode]); // eslint-disable-line

    /* ---------------- selected floor change ---------------- */
    useEffect(() => {
        const s = stateRef.current;
        if (!s || !s.ensureFloorsLoaded) return;
        s.selectedFloor = selectedFloor;
        (async () => {
            const view = snapshotView(s.camera, s.controls);

            if (mode === 'building') {
                s.applyOpacityForBuildingGroups(s.alphaByMode?.[mode]?.glb ?? 1.0);
                s.applyOpacityForStairs(s.alphaByMode?.[mode]?.stairs ?? 0.9);
                s.showAllFloorsVisible();
            } else {
                s.loadGen = (s.loadGen || 0) + 1;
                const gen = s.loadGen;
                s.unloadAllExcept([selectedFloor]);
                s.showOnlyFloorVisible(selectedFloor);
                await s.ensureFloorsLoaded([selectedFloor], gen, selectedFloor);
                s.applyOpacityForBuildingGroups(s.alphaByMode?.[mode]?.glb ?? 0.9);
                s.applyOpacityForStairs(s.alphaByMode?.[mode]?.stairs ?? 0.9);
                s.showOnlyFloorVisible(selectedFloor);
            }

            await s.loadStairs?.(s.loadGen);
            s.showStairsForFloor?.(s.selectedFloor || 'B1');

            if (s.baseFloorSectionsMesh && s.baseFloorSectionsMesh.size === 0) {
                await s.loadBaseSectionsMergedFromApi?.('/api/simulation/basesections');
            }

            // 미니맵 plane 높이 갱신 + 즉시 렌더
            const meta = s.floorMeta?.get(s.selectedFloor || 'B1') || {};
            const y0 = Number.isFinite(meta.y0) ? meta.y0 : 0;
            ['top', 'bottom'].forEach((k) => {
                const M = miniStateRef.current[k];
                if (!M?.plane) return;
                M.plane.position.y = y0 + overlayHeight + 0.02;
                M.renderer?.render(M.scene, M.cam);
            });

            restoreView(s.camera, s.controls, view);
            s.updateEdgeVisibility && s.updateEdgeVisibility();
        })();
    }, [selectedFloor, mode]); // eslint-disable-line

    /* ---------------- overlay textures position & opacity (메인) ---------------- */
    useEffect(() => {
        const s = stateRef.current;
        if (!s || !s.overlayPlanes) return;

        const A = s.alphaByMode?.[mode];
        const enabled = s.contourFloorModeEnabled ?? true;

        s.overlayPlanes.forEach((plane, f) => {
            const tex = overlayByFloor[f] || null;
            if (plane.material) {
                plane.material.map = tex;
                plane.material.opacity = A?.contour ?? 1.0;
                plane.material.needsUpdate = true;
            }

            const meta = s.floorMeta?.get(f);
            if (meta && plane && Number.isFinite(meta.y0)) {
                plane.position.y = meta.y0 + overlayHeight + 0.02;
            }

            const baseVisible = s.mode === 'floor' ? f === (s.selectedFloor || 'B1') && !!tex : !!tex;
            plane.visible = enabled && baseVisible;
        });
    }, [overlayByFloor, mode, selectedFloor, overlayHeight, contourFloorModeEnabled]);

    /* ---------------- visibility toggles ---------------- */
    useEffect(() => {
        const s = stateRef.current;
        if (!s) return;
        s.showBaseSections = showBaseSections;
        s.updateEdgeVisibility && s.updateEdgeVisibility();
    }, [showBaseSections]);
    useEffect(() => {
        const s = stateRef.current;
        if (!s) return;
        s.showRoutes = showRoutes;
        s.updateEdgeVisibility && s.updateEdgeVisibility();
    }, [showRoutes]);

    /* ---------------- alpha sync: UI(0-100) -> scene(0-1) ---------------- */
    useEffect(() => {
        const s = stateRef.current;
        if (!s) return;
        s.alphaByMode = {
            building: {
                glb: alphaUiByMode.building.glb / 100,
                stairs: alphaUiByMode.building.stairs / 100,
                sections: alphaUiByMode.building.sections / 100,
                routes: alphaUiByMode.building.routes / 100,
                contour: alphaUiByMode.building.contour / 100,
            },
            floor: {
                glb: alphaUiByMode.floor.glb / 100,
                stairs: alphaUiByMode.floor.stairs / 100,
                sections: alphaUiByMode.floor.sections / 100,
                routes: alphaUiByMode.floor.routes / 100,
                contour: alphaUiByMode.floor.contour / 100,
            },
        };

        const A = s.alphaByMode[mode];
        s.applyOpacityForBuildingGroups?.(A.glb);
        s.applyOpacityForStairs?.(A.stairs);

        s.baseFloorSectionsMesh?.forEach((mesh) => {
            const m = mesh?.material;
            if (!m) return;
            m.transparent = A.sections < 1.0;
            m.opacity = A.sections;
            m.depthWrite = !m.transparent;
            m.needsUpdate = true;
        });
        s.floorEdges?.forEach((val) => {
            (Array.isArray(val) ? val : [val]).forEach((mesh) => {
                const m = mesh?.material;
                if (!m) return;
                m.transparent = A.routes < 1.0;
                m.opacity = A.routes;
                m.needsUpdate = true;
            });
        });
        s.overlayPlanes?.forEach((plane) => {
            const m = plane?.material;
            if (!m) return;
            m.opacity = A.contour;
            m.needsUpdate = true;
        });
        s.baseFloorEdges?.forEach((mesh) => {
            const m = mesh?.material;
            if (!m) return;
            m.transparent = A.routes < 1.0;
            m.opacity = A.routes;
            m.needsUpdate = true;
        });

        s.applyRouteSelectionStyle?.();
    }, [alphaUiByMode, mode]);

    /* ---------------- planMode <-> camera switch ---------------- */
    useEffect(() => {
        const s = stateRef.current;
        if (!s) return;

        if (mode !== 'floor') {
            // 건물뷰: 항상 퍼스펙티브
            if (!s.camera?.isPerspectiveCamera) {
                s.switchToPerspective?.();
            }
            return;
        }
        if (planMode) s.switchToPlan?.();
        else s.switchToPerspective?.();
    }, [planMode, mode]);

    /* ---------------- 미니맵 텍스처 업데이트: 현재 지표 제외 두 지표 ---------------- */
    function otherTwoMetrics(key) {
        const ALL = ['co', 'temp', 'vis'];
        return ALL.filter((k) => k !== key);
    }
    useEffect(() => {
        const s = stateRef.current;
        if (!s) return;
        const [m1, m2] = otherTwoMetrics(currentMetric);
        const f = s.selectedFloor || selectedFloor;

        const tex1 = overlaysByMetric?.[m1]?.[f] || null;
        const tex2 = overlaysByMetric?.[m2]?.[f] || null;

        const apply = (slot, tex) => {
            const M = miniStateRef.current[slot];
            if (!M?.plane?.material) return;
            M.plane.material.map = tex || null;
            M.plane.material.opacity = tex ? 1.0 : 0.0;
            M.plane.material.needsUpdate = true;
            M.renderer?.render(M.scene, M.cam);
        };
        apply('top', tex1);
        apply('bottom', tex2);
    }, [currentMetric, overlaysByMetric, selectedFloor]);

    /* ---------------- render ---------------- */
    const labelOf = (k) => {
        if (k === 'co') return 'CO농도';
        if (k === 'temp') return '온도';
        if (k === 'vis') return '가시거리';
        return k?.toUpperCase?.() || '';
    };

    // (B) 현재 지표 제외 2개 구하기 (이미 선언된 otherTwoMetrics 사용)
    const [mTop, mBottom] = otherTwoMetrics(currentMetric);

    // (C) 미니맵 라벨 텍스트 (현재 지표가 없거나 초기 상태여도 기본값 제공)
    const labelTop = mTop ? labelOf(mTop) : '온도';
    const labelBottom = mBottom ? labelOf(mBottom) : '가시거리';
    const routeOptionsTop = mode === 'floor' ? 150 : 108;
    return (
        <div ref={mountRef} className={`${className ?? ''} ${styles.viewerRoot}`}>
            {/* 상단 우측: 모드/경로/평면모드 토글 */}
            <div className={styles.viewerTopRightPanel}>
                <div className={styles.viewerTopModeRow}>
                    <button
                        className={`${styles.btn} ${styles.btnSm} ${styles.viewerModeButton} ${mode === 'building' ? styles.viewerModeButtonActive : ''}`}
                        onClick={() => changeMode('building')}
                        aria-pressed={mode === 'building'}
                    >
                        건물뷰
                    </button>
                    <button
                        className={`${styles.btn} ${styles.btnSm} ${styles.viewerModeButton} ${mode === 'floor' ? styles.viewerModeButtonActive : ''}`}
                        onClick={() => changeMode('floor')}
                        aria-pressed={mode === 'floor'}
                    >
                        층뷰
                    </button>
                </div>

                {/* 대피경로 */}
                <label className={styles.viewerToggleRow}>
                    <input type="checkbox" checked={showRoutes} onChange={(e) => setShowRoutes(e.target.checked)} aria-checked={showRoutes} role="switch" />
                    대피경로
                </label>

                {/* 컨투어 바닥모드 */}
                <label className={styles.viewerToggleRow}>
                    <input type="checkbox" checked={contourFloorModeEnabled} onChange={(e) => setContourFloorModeEnabled(e.target.checked)} />
                    컨투어 바닥모드
                </label>

                {showRoutes && mode !== 'floor' && (
                    <>
                        <hr className={styles.viewerSeparator} />
                        <div className={styles.viewerRadioGroup}>
                            <label className={styles.viewerToggleRow}>
                                <input type="radio" name="routeViewMode" value="all" checked={routeViewMode === 'all'} onChange={() => setRouteViewMode('all')} />
                                전체 경로
                            </label>
                            <label className={styles.viewerToggleRow}>
                                <input
                                    type="radio"
                                    name="routeViewMode"
                                    value="fromSelectedFloor"
                                    checked={routeViewMode === 'fromSelectedFloor'}
                                    onChange={() => setRouteViewMode('fromSelectedFloor')}
                                />
                                층별 경로
                            </label>
                        </div>
                    </>
                )}

                {mode === 'floor' && (
                    <>
                        <hr className={styles.viewerSeparator} />
                        <label
                            className={`${styles.btn} ${styles.btnSm} ${styles.planModeLabel} ${planMode ? styles.planModeLabelActive : ''}`}
                            title="평면도처럼 보기(정투영 탑다운)"
                        >
                            <input
                                type="checkbox"
                                checked={planMode}
                                onChange={(e) => setPlanMode(e.target.checked)}
                                aria-checked={planMode}
                                role="switch"
                                className={styles.planModeCheckbox}
                            />
                            평면모드
                        </label>
                    </>
                )}
            </div>

            {/* === MiniMaps === */}
            <div className={`${styles.miniGroup} ${styles.miniGroupFirst}`}>
                {!isMobile && <div className={styles.miniLabelFirst}>{labelTop}</div>}
                <canvas ref={miniTopRef} width={150} height={150} className={styles.miniCanvas} />
                {isMobile && <div className={styles.miniLabelFirst}>{labelTop}</div>}
            </div>

            <div className={`${styles.miniGroup} ${styles.miniGroupSecond}`}>
                <canvas ref={miniBottomRef} width={150} height={150} className={styles.miniCanvas} />
                <div className={styles.miniLabelSecond}>{labelBottom}</div>
            </div>
            {SHOW_OPACITY_DEBUG_PANEL && (
                <div
                    id="opacity-panel"
                    className={`${styles.opacityPanel} ${panelOpen ? styles.opacityPanelOpen : styles.opacityPanelClosed}`}
                    aria-hidden={!panelOpen}
                    aria-expanded={panelOpen}
                >
                    <div className={styles.opacityPanelHeader}>
                        <div className={styles.opacityPanelTitle}>투명도 조절 ({mode === 'building' ? '건물뷰' : '층뷰'})</div>
                        <button onClick={() => setPanelOpen((v) => !v)} className={styles.opacityPanelToggleBtn} title={panelOpen ? '숨기기' : '보이기'}>
                            {panelOpen ? '숨기기' : '보이기'}
                        </button>
                    </div>

                    <OpacitySlider label="건물(GLB)" value={curAlphaUi.glb} onChange={(val) => setAlphaUiByMode((p) => ({ ...p, [mode]: { ...p[mode], glb: val } }))} />
                    <OpacitySlider label="계단(GLB)" value={curAlphaUi.stairs} onChange={(val) => setAlphaUiByMode((p) => ({ ...p, [mode]: { ...p[mode], stairs: val } }))} />
                    <OpacitySlider label="바닥 섹션" value={curAlphaUi.sections} onChange={(val) => setAlphaUiByMode((p) => ({ ...p, [mode]: { ...p[mode], sections: val } }))} />
                    <OpacitySlider label="대피 경로" value={curAlphaUi.routes} onChange={(val) => setAlphaUiByMode((p) => ({ ...p, [mode]: { ...p[mode], routes: val } }))} />
                    <OpacitySlider label="컨투어" value={curAlphaUi.contour} onChange={(val) => setAlphaUiByMode((p) => ({ ...p, [mode]: { ...p[mode], contour: val } }))} />
                </div>
            )}

            {SHOW_HUDVIEW_DEBUG_PANEL && hudView && (
                <div className={styles.hudContainer}>
                    <div className={styles.hudText}>
                        <div>pos: [{hudView.pos.map((v) => v.toFixed(3)).join(', ')}]</div>
                        <div>target: [{hudView.target.map((v) => v.toFixed(3)).join(', ')}]</div>
                    </div>
                </div>
            )}
        </div>
    );
}

/* ------------------------------ UI: Slider ------------------------------ */
function OpacitySlider({ label, value, onChange }) {
    return (
        <div className={styles.opacitySlider}>
            {' '}
            <div className={styles.opacitySliderLabel}>{label}</div> <div className={styles.opacitySliderValue}>{value}%</div>{' '}
            <input type="range" min={0} max={100} step={1} value={value} onChange={(e) => onChange(Number(e.target.value))} className={styles.opacitySliderRange} />{' '}
        </div>
    );
}
